import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { getInterviewStatus } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

/**
 * Side-door hold for client routes that are not already gated by ClientDashboard.
 * Children paint only when status explicitly says complete — an incomplete first
 * meeting never opens the SIEM list, the world console, chat or settings.
 *
 * Where an incomplete client LEAVES to is the product decision here. This used
 * to replace-navigate to /client/interview, which made the first meeting a
 * second product: a household that hit a side door (/client/alerts,
 * /client/world, /client/concierge, …) was dropped straight into the interview
 * and never reached /client home — so it never saw HomeTripwiresCard and never
 * saw "Download Decoy (.docx)". DigitalWatch.md: canaries are in the same
 * picture, a tripwire the analyst sees with the logs — not a gimmick hidden
 * behind an unfinished meeting. So the leave is /client: ClientDashboard
 * already keeps an incomplete meeting ON home with the Word decoy download in
 * that home section, and /client is not wrapped by this gate, so the leave
 * settles there instead of bouncing.
 *
 * A status throw is not a completed meeting either → same leave to /client; a
 * flaky status check must never cost the household the canary. No principal →
 * hold (unknown stays unknown; nothing paints, nothing is fetched). Never start
 * a meeting here; status is read-only.
 */
type Gate = 'pending' | 'allow' | 'leave'

export function ClientInterviewGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const principalId = useAuthStore((s) => s.principal?.id)
  const [gate, setGate] = useState<Gate>('pending')

  useEffect(() => {
    if (!principalId) {
      // No principal → do not consult status; children stay held.
      setGate('pending')
      return
    }
    let cancelled = false
    setGate('pending')
    void (async () => {
      try {
        const st = await getInterviewStatus()
        if (cancelled) return
        if (st.status === 'complete') {
          setGate('allow')
        } else {
          // Incomplete/unknown → home, where the first meeting lives WITH the
          // tripwire band and the Word decoy download. Not /client/interview.
          setGate('leave')
          navigate('/client', { replace: true })
        }
      } catch {
        // A status throw is not a completed meeting → the meeting is home, and
        // home is /client so the canary stays in the same picture.
        if (!cancelled) {
          setGate('leave')
          navigate('/client', { replace: true })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [principalId, navigate])

  if (gate !== 'allow') {
    return null
  }
  return <>{children}</>
}
